// pages/admin/AdminLogin.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Typography,
  Container,
  Box,
  TextField,
  Button,
  Alert,
  Paper,
  Chip,
  Fade,
  InputAdornment,
  IconButton,
  Divider,
  Stack,
  Link,
} from "@mui/material";
import {
  Email,
  Lock,
  Visibility,
  VisibilityOff,
  AdminPanelSettings,
  ArrowForward,
  Verified,
  Security,
} from "@mui/icons-material";
import { postData } from "../../services/Request";
import CircularProgress from "@mui/material/CircularProgress";
import Logo from "../../assets/Header/logo.png";

export default function AdminLogIn() {
  const nav = useNavigate();

  const [alertMessage, setAlertMessage] = useState("");
  const [severity, setSeverity] = useState("");
  const [loader, setLoader] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  useEffect(() => {
    // Check if admin is already logged in
    const user = localStorage.getItem("user");
    if (user) {
      try {
        const data = JSON.parse(user);
        if (data && data.usertype === "Admin") {
          nav("/studentTable");
        }
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }
  }, [nav]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      setEmailError("Email is required");
      return false;
    } else if (!emailRegex.test(email)) {
      setEmailError("Please enter a valid email address");
      return false;
    }
    setEmailError("");
    return true;
  };

  const validatePassword = (password) => {
    if (!password) {
      setPasswordError("Password is required");
      return false;
    } else if (password.length < 6) {
      setPasswordError("Password must be at least 6 characters");
      return false;
    }
    setPasswordError("");
    return true;
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    if (value) validateEmail(value);
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    if (value) validatePassword(value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const isEmailValid = validateEmail(email);
    const isPasswordValid = validatePassword(password);

    if (!isEmailValid || !isPasswordValid) {
      return;
    }

    setLoader(true);
    setAlertMessage("");

    const details = { email, password, usertype: "Admin" };

    postData("instuctorlogin", details)
      .then((response) => {
        setLoader(false);
        if (response.status === true && response.user.usertype === "Admin") {
          localStorage.setItem("user", JSON.stringify(response.user));
          setAlertMessage("✅ Logging you in...");
          setSeverity("success");
          setTimeout(() => {
            nav("/studentTable");
          }, 500);
        } else {
          setAlertMessage("❌ " + (response.message || "Invalid credentials"));
          setSeverity("error");
        }
      })
      .catch((error) => {
        setLoader(false);
        setAlertMessage(
          "❌ Error logging you in, please check your account is activated",
        );
        setSeverity("error");
        console.error("Login error:", error);
      });
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      className="admin-login-container"
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #F7F8F2 0%, #e8efe6 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        py: { xs: 4, md: 0 },
      }}
    >
      {/* Animated Background Decorations */}
      <Box
        sx={{
          position: "absolute",
          top: -200,
          right: -200,
          width: 500,
          height: 500,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(43, 173, 101, 0.04) 0%, transparent 70%)",
          zIndex: 0,
          animation: "pulse 8s ease-in-out infinite",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: -200,
          left: -200,
          width: 500,
          height: 500,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(160, 239, 72, 0.03) 0%, transparent 70%)",
          zIndex: 0,
          animation: "pulse 8s ease-in-out infinite 2s",
        }}
      />

      {/* Floating Particles */}
      {[...Array(4)].map((_, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            top: `${10 + i * 25}%`,
            left: `${5 + (i % 3) * 30}%`,
            width: `${4 + i * 2}px`,
            height: `${4 + i * 2}px`,
            borderRadius: "50%",
            backgroundColor:
              i % 2 === 0
                ? "rgba(43, 173, 101, 0.12)"
                : "rgba(160, 239, 72, 0.12)",
            animation: `float ${6 + i * 2}s ease-in-out infinite ${i * 1.5}s`,
            zIndex: 0,
          }}
        />
      ))}

      <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4, md: 5 },
            borderRadius: 4,
            backgroundColor: "rgba(255, 255, 255, 0.85)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(43, 173, 101, 0.06)",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.04)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Animated Gradient Border */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 3,
              background: "linear-gradient(90deg, #2BAD65, #A0EF48, #2BAD65)",
              backgroundSize: "200% 100%",
              animation: "shimmer 3s linear infinite",
            }}
          />

          {/* Logo */}
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Box
              component="img"
              src={Logo}
              alt="Wavepalm Techub"
              sx={{
                height: 48,
                width: "auto",
                mx: "auto",
                transition: "transform 0.3s ease",
                "&:hover": {
                  transform: "scale(1.05)",
                },
              }}
            />
          </Box>

          {/* Header */}
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Chip
              icon={<AdminPanelSettings sx={{ fontSize: 16 }} />}
              label="Admin Login"
              sx={{
                backgroundColor: "rgba(43, 173, 101, 0.1)",
                color: "#2BAD65",
                fontWeight: 600,
                mb: 2,
                border: "1px solid rgba(43, 173, 101, 0.15)",
              }}
            />
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: "#1a1a1a",
                fontSize: { xs: "1.8rem", md: "2.2rem" },
              }}
            >
              Welcome Back!
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "rgba(26, 26, 26, 0.5)",
                mt: 0.5,
              }}
            >
              Access the admin dashboard to manage your platform
            </Typography>
          </Box>

          {/* Trust Badges */}
          <Stack
            direction="row"
            spacing={2}
            justifyContent="center"
            sx={{ mb: 3, flexWrap: "wrap", gap: 1 }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Security sx={{ color: "#2BAD65", fontSize: 16 }} />
              <Typography
                variant="caption"
                sx={{ color: "rgba(26, 26, 26, 0.3)" }}
              >
                Secure Login
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Verified sx={{ color: "#2BAD65", fontSize: 16 }} />
              <Typography
                variant="caption"
                sx={{ color: "rgba(26, 26, 26, 0.3)" }}
              >
                Admin Access
              </Typography>
            </Box>
          </Stack>

          {/* Alert Message */}
          {alertMessage && (
            <Fade in timeout={300}>
              <Alert
                variant="filled"
                severity={severity}
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  backgroundColor:
                    severity === "success" ? "#2BAD65" : "#FF6B6B",
                  "& .MuiAlert-icon": {
                    color: "#ffffff",
                  },
                }}
              >
                {alertMessage}
              </Alert>
            </Fade>
          )}

          {/* Form */}
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={handleEmailChange}
              error={!!emailError}
              helperText={emailError}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: "rgba(26, 26, 26, 0.2)" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "rgba(43, 173, 101, 0.04)",
                  color: "#1a1a1a",
                  borderRadius: 2,
                  "& fieldset": {
                    borderColor: "rgba(43, 173, 101, 0.08)",
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(43, 173, 101, 0.15)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#2BAD65",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "rgba(26, 26, 26, 0.4)",
                },
                "& .MuiFormHelperText-root": {
                  color: "#FF6B6B",
                },
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? "text" : "password"}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={handlePasswordChange}
              error={!!passwordError}
              helperText={passwordError}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: "rgba(26, 26, 26, 0.2)" }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleTogglePassword}
                      edge="end"
                      sx={{ color: "rgba(26, 26, 26, 0.2)" }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "rgba(43, 173, 101, 0.04)",
                  color: "#1a1a1a",
                  borderRadius: 2,
                  "& fieldset": {
                    borderColor: "rgba(43, 173, 101, 0.08)",
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(43, 173, 101, 0.15)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#2BAD65",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "rgba(26, 26, 26, 0.4)",
                },
                "& .MuiFormHelperText-root": {
                  color: "#FF6B6B",
                },
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loader}
              startIcon={!loader ? <ArrowForward /> : null}
              sx={{
                mt: 3,
                mb: 2,
                py: 1.5,
                fontSize: "1rem",
                fontWeight: 700,
                background: "linear-gradient(135deg, #2BAD65, #A0EF48)",
                borderRadius: 2,
                color: "#ffffff",
                boxShadow: "0 4px 25px rgba(43, 173, 101, 0.3)",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 35px rgba(43, 173, 101, 0.4)",
                  background: "linear-gradient(135deg, #239A56, #8EDB40)",
                },
                "&:disabled": {
                  backgroundColor: "rgba(43, 173, 101, 0.15)",
                },
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              {loader ? (
                <CircularProgress size={24} sx={{ color: "#ffffff" }} />
              ) : (
                "Log In"
              )}
            </Button>

            <Divider
              sx={{ my: 2, backgroundColor: "rgba(43, 173, 101, 0.06)" }}
            >
              <Typography
                variant="caption"
                sx={{ color: "rgba(26, 26, 26, 0.2)", px: 2 }}
              >
                Admin Access Only
              </Typography>
            </Divider>

            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="caption"
                sx={{ color: "rgba(26, 26, 26, 0.2)" }}
              >
                This area is restricted to authorized administrators only.
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>

      <style>
        {`
          @keyframes pulse {
            0%, 100% { transform: scale(1); opacity: 0.3; }
            50% { transform: scale(1.1); opacity: 0.6; }
          }

          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
          }

          @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }

          .admin-login-container {
            min-height: 100vh;
          }
        `}
      </style>
    </Box>
  );
}
